import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { DiscountModel } from '~/models/DiscountModel';
import { MatchConfidence } from '~/models/matchConfidence';
import { utils } from '~/utils';
export class ExtractedServiceModel {
    dto() {
        return this.service;
    }
    displayName() {
        const s = this.service;
        if (s.matchStatus === 'matched' && s.matchConfidence === MatchConfidence.High) {
            return s.matchedServiceTemplateName;
        }
        return s.name;
    }
    showNewServiceBadge() {
        const s = this.service;
        if (s.matchStatus === 'matched' && s.matchConfidence === MatchConfidence.High) return false;
        return true;
    }
    suggestionMessage(services) {
        if (this.service.matchStatus !== 'matched') return null;
        if (this.service.matchConfidence === MatchConfidence.High) {
            if (this.service.name === this.service.matchedServiceTemplateName) return null;
            return {
                emphasizedName: this.service.name,
                suffix: services.messageWasMatchedSuffix(),
                actionLabel: services.actionUseFileVersion(),
                action: 'useFileVersion'
            };
        }
        if (this.service.matchConfidence === MatchConfidence.Medium) {
            return {
                prefix: services.messageMightMatchPrefix(),
                emphasizedName: this.service.matchedServiceTemplateName,
                actionLabel: services.actionUseTemplate(),
                action: 'useTemplate'
            };
        }
        return null;
    }
    priceDisplay(services, discountStandard) {
        const pricing = this.completePricing();
        if (pricing == null) return {
            priceAfterDiscount: services.rateToBeAdded()
        };
        return priceDisplayForPricing(pricing, services, discountStandard);
    }
    isPriceMissing() {
        return this.completePricing() == null;
    }
    billingTriggerLabel(services) {
        const trigger = this.service.billingTrigger;
        if (trigger == null) return null;
        return triggerLabel(trigger, services);
    }
    completePricing() {
        var _this_service_billingTrigger;
        const pricing = (_this_service_billingTrigger = this.service.billingTrigger) === null || _this_service_billingTrigger === void 0 ? void 0 : _this_service_billingTrigger.pricing;
        if (pricing == null) return null;
        if (pricing.type === 'fixed') return pricing.price != null ? pricing : null;
        if (pricing.type === 'variable') {
            return pricing.unitPrice != null && pricing.unitName != null ? pricing : null;
        }
        return pricing.minPrice != null && pricing.maxPrice != null ? pricing : null;
    }
    constructor(service){
        _define_property(this, "service", void 0);
        this.service = service;
    }
}
function priceDisplayForPricing(pricing, services, discountStandard) {
    switch(pricing.type){
        case 'fixed':
            {
                if (pricing.price == null) return {
                    priceAfterDiscount: services.rateToBeAdded()
                };
                const discount = extractionDiscountToModel(pricing.discount, pricing.price);
                const discountedPrice = utils.applyDiscount(pricing.price, discount).toNumber();
                return {
                    priceAfterDiscount: services.priceFixed({
                        price: utils.formatDollarAmount(discountedPrice)
                    }),
                    priceBeforeDiscount: discount ? utils.formatDollarAmount(pricing.price) : undefined,
                    discountBadgeText: discount ? discountStandard({
                        amount: discount.format()
                    }) : undefined
                };
            }
        case 'variable':
            {
                if (pricing.unitPrice == null || pricing.unitName == null) {
                    return {
                        priceAfterDiscount: services.rateToBeAdded()
                    };
                }
                const discount = extractionDiscountToModel(pricing.discount, pricing.unitPrice);
                const discountedUnitPrice = utils.applyDiscount(pricing.unitPrice, discount).toNumber();
                return {
                    priceAfterDiscount: services.priceVariable({
                        unitPrice: utils.formatDollarAmount(discountedUnitPrice),
                        unitName: pricing.unitName
                    }),
                    priceBeforeDiscount: discount ? utils.formatDollarAmount(pricing.unitPrice) : undefined,
                    discountBadgeText: discount ? discountStandard({
                        amount: discount.format()
                    }) : undefined
                };
            }
        case 'range':
            {
                if (pricing.minPrice == null || pricing.maxPrice == null) {
                    return {
                        priceAfterDiscount: services.rateToBeAdded()
                    };
                }
                const discount = extractionDiscountToModel(pricing.discount, pricing.maxPrice);
                const discountedMin = utils.applyDiscount(pricing.minPrice, discount).toNumber();
                const discountedMax = utils.applyDiscount(pricing.maxPrice, discount).toNumber();
                return {
                    priceAfterDiscount: services.priceRange({
                        minPrice: utils.formatDollarAmount(discountedMin),
                        maxPrice: utils.formatDollarAmount(discountedMax)
                    }),
                    priceBeforeDiscount: discount ? services.priceRange({
                        minPrice: utils.formatDollarAmount(pricing.minPrice),
                        maxPrice: utils.formatDollarAmount(pricing.maxPrice)
                    }) : undefined,
                    discountBadgeText: discount ? discountStandard({
                        amount: discount.format()
                    }) : undefined
                };
            }
    }
}
function extractionDiscountToModel(discount, price) {
    if (discount == null || discount.type === 'none' || discount.value === 0) return null;
    if (discount.type === 'fixed' && discount.value >= price) return null;
    return discount.type === 'percentage' ? DiscountModel.makePercent(discount.value) : DiscountModel.makeValue(discount.value);
}
function triggerLabel(trigger, services) {
    if (trigger.type === 'automaticallyRecurring') {
        if (trigger.recurrencePeriod != null) {
            return services.billingAutomaticallyRecurringWithPeriod({
                period: trigger.recurrencePeriod
            });
        }
        return services.billingAutomaticallyRecurring();
    }
    if (trigger.type === 'manuallyRecurring') {
        if (trigger.recurrencePeriod != null) {
            return services.billingManuallyRecurringWithPeriod({
                period: trigger.recurrencePeriod
            });
        }
        return services.billingManuallyRecurring();
    }
    if (trigger.type === 'oneTimeManual') return services.billingOneTimeManual();
    if (trigger.type === 'oneTimeOnApproval') return services.billingOneTimeOnApproval();
    if (trigger.type === 'oneTimeInStages') return services.billingOneTimeInStages();
    return services.billingOnDemand();
}
